/**
 * Product / upload image URL helpers (local /upload, MinIO, or external URLs)
 */

export const DEFAULT_PRODUCT_IMAGE = './placeholder-product.jpg';
export const FALLBACK_IMAGE = './placeholder-product.jpg';

const DEFAULT_MINIO_BUCKET =
  (import.meta.env && import.meta.env.VITE_MINIO_BUCKET) ||
  (typeof process !== 'undefined' && process.env && process.env.REACT_APP_MINIO_BUCKET) ||
  'greenguest-uploads';

export function getBackendBaseUrl(): string {
  let backendUrl =
    (import.meta.env && import.meta.env.VITE_API_URL) ||
    (typeof process !== 'undefined' && process.env && process.env.REACT_APP_API_URL) ||
    'https://api.greenguestbd.com/api';

  if (backendUrl.endsWith('/api')) {
    backendUrl = backendUrl.slice(0, -4);
  } else if (backendUrl.includes('/api/')) {
    backendUrl = backendUrl.replace('/api', '');
  }

  return backendUrl.replace(/\/$/, '');
}

/** Public MinIO base URL (browser-accessible) */
export function getMinioPublicUrl(): string {
  const fromEnv =
    (import.meta.env && import.meta.env.VITE_MINIO_PUBLIC_URL) ||
    (typeof process !== 'undefined' && process.env && process.env.REACT_APP_MINIO_PUBLIC_URL);
  if (fromEnv) {
    return fromEnv.replace(/\/$/, '');
  }
  return 'http://localhost:9000';
}

/**
 * Resolves stored image paths to a browser-loadable URL.
 * - MinIO http:// URLs → proxied via HTTPS backend (/api/images/<key>)
 * - Legacy /upload/ paths → backend base URL
 * - Already-https URLs → returned as-is
 */
export const getProductImage = (imageUrl?: string): string => {
  if (!imageUrl || imageUrl.trim() === '') {
    return DEFAULT_PRODUCT_IMAGE;
  }

  // Remove stray quotes in case URL was saved with them
  const trimmed = imageUrl.replace(/^["']|["']$/g, '').trim();

  if (trimmed.startsWith('http://') || trimmed.startsWith('https://')) {
    try {
      const url = new URL(trimmed);
      const parts = url.pathname.split('/').filter(Boolean);
      const bucketIndex = parts.indexOf(DEFAULT_MINIO_BUCKET);

      if (bucketIndex >= 0) {
        // Extract the object key (everything after the bucket name)
        const objectKey = parts.slice(bucketIndex + 1).join('/');
        // Proxy through our HTTPS backend to avoid mixed-content block
        const backendBase = getBackendBaseUrl();
        return `${backendBase}/api/images/${objectKey}`;
      }
    } catch {
      // ignore parse errors — fall through to return as-is
    }
    // Already an https URL that isn't a raw MinIO URL — return as-is
    return trimmed;
  }

  if (trimmed.startsWith('/upload/')) {
    return `${getBackendBaseUrl()}${trimmed}`;
  }

  if (trimmed.startsWith('data:')) {
    return trimmed;
  }

  return trimmed;
};


/**
 * Object key for DELETE /upload/image/:fileName (MinIO or legacy path).
 */
export function extractStorageObjectKey(urlOrPath: string): string {
  if (!urlOrPath) {
    return '';
  }

  const trimmed = urlOrPath.replace(/^["']|["']$/g, '').trim();

  if (trimmed.startsWith('http://') || trimmed.startsWith('https://')) {
    try {
      const url = new URL(trimmed);
      const parts = url.pathname.split('/').filter(Boolean);
      const bucketIndex = parts.indexOf(DEFAULT_MINIO_BUCKET);
      if (bucketIndex >= 0 && bucketIndex < parts.length - 1) {
        return decodeURIComponent(parts.slice(bucketIndex + 1).join('/'));
      }
      return decodeURIComponent(parts[parts.length - 1] || '');
    } catch {
      return trimmed;
    }
  }

  if (trimmed.startsWith('/upload/')) {
    return trimmed.replace(/^\/upload\//, '');
  }

  return trimmed.replace(/^\//, '');
}

export function isManagedStorageUrl(url: string): boolean {
  if (!url) {
    return false;
  }
  const cleanUrl = url.replace(/^["']|["']$/g, '').trim();
  if (cleanUrl.startsWith('/upload/')) {
    return true;
  }
  if (cleanUrl.includes(`/${DEFAULT_MINIO_BUCKET}/`)) {
    return true;
  }
  const minioBase = getMinioPublicUrl();
  if (minioBase && cleanUrl.startsWith(minioBase)) {
    return true;
  }
  return false;
}

/** Preview in developer modal: data URLs as-is, otherwise resolve for display */
export function resolvePreviewImageSrc(
  preview: string | null | undefined,
  storedUrl?: string,
): string | undefined {
  if (preview?.startsWith('data:')) {
    return preview;
  }
  const value = preview || storedUrl;
  if (!value) {
    return undefined;
  }
  return getProductImage(value);
}

export const handleImageError = (
  event: React.SyntheticEvent<HTMLImageElement, Event>,
): void => {
  const target = event.target as HTMLImageElement;
  target.src = FALLBACK_IMAGE;
  target.onerror = null;
};

export const isValidImageUrl = (url?: string): boolean => {
  if (!url || url.trim() === '') return false;

  try {
    const urlObj = new URL(url);
    return urlObj.protocol === 'http:' || urlObj.protocol === 'https:';
  } catch {
    return false;
  }
};
